import { Box, Text, VStack, HStack, Image } from "@chakra-ui/react";
import { Line } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    type TooltipItem
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { ThemeColor } from "../../theme/palette";
import { AirdropChecker } from "../../rest/kairos/types";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    annotationPlugin
);

interface AirdropValueProps {
    checker: AirdropChecker;
    minFdv?: number; // in millions
    maxFdv?: number; // in millions
    numPoints?: number;
    totalSupply?: number; // in millions
    price?: number;
}

export const AirdropValue = ({
    checker,
    minFdv = 5, // default 5M
    maxFdv = 100, // default 100M
    numPoints = 20,
    totalSupply = 50, // default 50M
    price = 0.1
}: AirdropValueProps) => {
    const userTokens = checker ? (checker.amount / 10 ** 9) : 0;

    // Competitor vertical lines configuration
    const competitors = [
        { name: 'Fragmetric', fdvM: 25, image: '/fragmetric.png' },
        { name: 'Renzo', fdvM: 125, image: '/renzo.png' },
        // { name: 'Solayer', fdvM: 247, image: '/solayer.png' },
    ];
    const competitorLineColors = [
        'rgba(255, 255, 255, 1)',
        '#C1FE60',
        // '#084D3E'
    ];

    // Generate FDV points from minFdv to maxFdv
    const step = (maxFdv - minFdv) / (numPoints - 1);
    const fdvPoints = Array.from(
        { length: numPoints },
        (_, i) => (minFdv + (i * step)) * 1000000 // convert M to actual value
    );

    // Calculate token value at each FDV point
    const valuePoints = fdvPoints.map(fdv => {
        const tokenPrice = fdv / (totalSupply * 1000000); // convert totalSupply to actual value
        return (userTokens * tokenPrice).toFixed(2);
    });

    const data = {
        labels: fdvPoints.map(fdv => `$${(fdv / 1000000).toFixed(0)}M`),
        datasets: [
            {
                label: 'Your Airdrop Value',
                data: valuePoints,
                borderColor: '#5FF4F4',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                tension: 0.4,
                fill: true,
                pointRadius: (ctx: any) => {
                    const currentFdv = price * totalSupply * 1000000;
                    const closestFdv = fdvPoints.reduce((prev, curr) => {
                        return Math.abs(curr - currentFdv) < Math.abs(prev - currentFdv) ? curr : prev;
                    }, fdvPoints[0]);
                    const closestFdvIndex = fdvPoints.findIndex(fdv => fdv === closestFdv);
                    return ctx.dataIndex === closestFdvIndex ? 6 : 0;
                },
                pointHoverRadius: 6,
                pointBackgroundColor: '#5FF4F4',
                pointBorderColor: ThemeColor.KAIROS,
                pointBorderWidth: 2,
                pointHoverBackgroundColor: '#5FF4F4',
                pointHoverBorderColor: ThemeColor.KAIROS,
                pointHoverBorderWidth: 2,
                borderWidth: 2,
            }
        ]
    };

    // Find closest label position for each competitor FDV to align vertical lines on the categorical x-axis
    const competitorsWithLabels = competitors.map(c => {
        const targetFdv = c.fdvM * 1000000;
        const closestFdv = fdvPoints.reduce((prev, curr) => {
            return Math.abs(curr - targetFdv) < Math.abs(prev - targetFdv) ? curr : prev;
        }, fdvPoints[0]);
        const xLabel = `$${(closestFdv / 1000000).toFixed(0)}M`;
        return { ...c, xLabel };
    });

    const annotationEntries = Object.fromEntries(
        competitorsWithLabels.map((c, index) => [
            c.name,
            {
                type: 'line',
                xMin: c.xLabel,
                xMax: c.xLabel,
                borderColor: competitorLineColors[index],
                borderWidth: 2,
                borderDash: [6, 6],
            }
        ])
    );

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index'
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(12, 17, 23, 0.95)',
                titleColor: "white",
                bodyColor: 'white',
                borderColor: 'transparent',
                borderWidth: 0,
                cornerRadius: 8,
                displayColors: false,
                titleFont: {
                    family: 'Poppins, sans-serif',
                    size: 16,
                    weight: 700 // Use number instead of 'bold'
                },
                bodyFont: {
                    family: 'Poppins, sans-serif',
                    size: 12,
                    weight: 400 // Use number instead of 'normal'
                },
                callbacks: {
                    title: (context: TooltipItem<'line'>[]) => `FDV: ${context[0].label}`,
                    label: (context: TooltipItem<'line'>) => `Value: $${Number(context.raw).toLocaleString()}`
                },
                padding: 12
            },
            annotation: {
                annotations: annotationEntries as any
            }
        },
        scales: {
            x: {
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
                ticks: {
                    color: 'white',
                    maxRotation: 45,
                    minRotation: 45,
                    font: {
                        family: 'Poppins, sans-serif',
                        size: 12
                    }
                }
            },
            y: {
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
                ticks: {
                    color: 'white',
                        callback: (value: number | string, index: number) => {
                            if (index === 0) {
                                return `$ ${Math.round(userTokens * 0.1).toLocaleString('de-CH', { maximumFractionDigits: 0 })}`;
                            }
                            return `$ ${Number(value).toLocaleString('de-CH', { maximumFractionDigits: 0 })}`;
                        },
                    font: {
                        family: 'Poppins, sans-serif',
                        size: 12
                    }
                }
            }
        }
    };

    return (
        <Box
            p={6}
            borderRadius="8px"
            backgroundColor="whiteAlpha.100"
        >
            <VStack spacing={6} align="stretch">
                <Text fontSize="xl" fontWeight="bold" color="white" textAlign="center">
                    Your Airdrop Value
                </Text>

                {checker ? (
                    <VStack spacing={3} align="stretch">
                        <Box height="400px">
                            <Line data={data} options={options} />
                        </Box>
                        <Box mt={3} display="flex" justifyContent="center">
                            <HStack spacing={6}>
                                {competitorsWithLabels.map((c) => (
                                    <HStack key={c.name} spacing={2}>
                                        <Image src={c.image} alt={c.name} boxSize="15px" borderRadius="full" />
                                        <Text color="white" fontSize="xs">{c.name} — ${c.fdvM}M</Text>
                                    </HStack>
                                ))}
                            </HStack>
                        </Box>
                        <Text color="white" fontSize="md" fontWeight="bold" textAlign="center" mt={4}>$KYROS FDV - ${Math.round(price * totalSupply)}M</Text>
                    </VStack>
                ) : (
                    <Box p={4} borderRadius="8px" backgroundColor="whiteAlpha.200" color="white">
                        <Text textAlign="center">Connect your wallet to view your airdrop value</Text>
                    </Box>
                )}
            </VStack>
        </Box>
    );
};
